import { useEffect, useState } from 'react';
import axios from 'axios';
import { getHeaders } from './getHeader';
import { getUserType } from './auth';

// admin is never governed by this system - it stays fully unrestricted,
// exactly like PrivateRoute.js's "admin can access all routes". Every other
// role goes through the per-user page permission check.
const PERMISSION_GOVERNED_ROLES = [
  'cashier',
  'promoter',
  'account',
  'cstoremanager',
  'wstoremanager',
  'modifier',
  'hr',
];

let cachedPageMap = null; // code -> effective
let cachedPathMap = null; // path -> effective
let inflightPromise = null;

const fetchEffectivePermissions = () => {
  if (cachedPageMap) return Promise.resolve({ map: cachedPageMap, pathMap: cachedPathMap });
  if (inflightPromise) return inflightPromise;

  inflightPromise = axios
    .get('/page-permissions/effective/me', getHeaders())
    .then((res) => {
      const pages = res?.data?.pages || [];
      cachedPageMap = new Map(pages.map((p) => [p.code, !!p.effective]));
      // Every page_registry row carries its own route path (see
      // pagePermissionController.js's buildEffectivePermissions), so the
      // sidebar/route guard can gate by path without needing their own
      // hardcoded copy of the code<->path mapping.
      cachedPathMap = new Map(pages.filter((p) => p.path).map((p) => [p.path, !!p.effective]));
      return { map: cachedPageMap, pathMap: cachedPathMap };
    })
    .catch((err) => {
      // Fail open for this one call (empty maps => everything resolves
      // allowed) rather than locking a governed user out over a network
      // hiccup - but don't cache the failure, or a transient blip would
      // silently disable every permission check for the rest of the session.
      console.error('Error fetching page permissions:', err);
      return { map: new Map(), pathMap: new Map() };
    })
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
};

// Call after an Admin changes a user's own permissions (or on logout) so the
// next check re-fetches instead of serving a stale cached map.
export const clearPagePermissionsCache = () => {
  cachedPageMap = null;
  cachedPathMap = null;
  inflightPromise = null;
};

// React hook - gates a single page (e.g. 'reports.table_history') for the
// current logged-in user. Roles outside PERMISSION_GOVERNED_ROLES resolve
// synchronously to allowed=true, no network call.
export const usePagePermission = (pageCode) => {
  const userType = (getUserType() || '').toLowerCase();
  const isGoverned = PERMISSION_GOVERNED_ROLES.includes(userType);

  const [allowed, setAllowed] = useState(!isGoverned);
  const [loading, setLoading] = useState(isGoverned);

  useEffect(() => {
    if (!isGoverned) {
      setAllowed(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchEffectivePermissions().then(({ map }) => {
      if (cancelled) return;
      // A page code the backend doesn't recognize (or doesn't apply to this
      // role) defaults to allowed, so it never accidentally hides an
      // unrelated page.
      setAllowed(map.has(pageCode) ? map.get(pageCode) : true);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCode, isGoverned]);

  return { allowed, loading };
};

// Same as usePagePermission but looks the current user up by route path
// instead of a hardcoded page code - used by PrivateRoute.js, which only
// knows the path the user is trying to reach.
export const usePagePathPermission = (path) => {
  const userType = (getUserType() || '').toLowerCase();
  const isGoverned = PERMISSION_GOVERNED_ROLES.includes(userType);

  const [allowed, setAllowed] = useState(!isGoverned);
  const [loading, setLoading] = useState(isGoverned);

  useEffect(() => {
    if (!isGoverned) {
      setAllowed(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchEffectivePermissions().then(({ pathMap }) => {
      if (cancelled) return;
      setAllowed(pathMap.has(path) ? pathMap.get(path) : true);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [path, isGoverned]);

  return { allowed, loading };
};

// React hook - same governed-roles/caching behavior as usePagePermission, but
// returns the whole effective-permission maps (by code and by path) at once
// instead of a single lookup. Useful for filtering the sidebar in one pass.
export const usePagePermissionsMap = () => {
  const userType = (getUserType() || '').toLowerCase();
  const isGoverned = PERMISSION_GOVERNED_ROLES.includes(userType);

  const [map, setMap] = useState(() => new Map());
  const [pathMap, setPathMap] = useState(() => new Map());
  const [loading, setLoading] = useState(isGoverned);

  useEffect(() => {
    if (!isGoverned) {
      setMap(new Map());
      setPathMap(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchEffectivePermissions().then((resolved) => {
      if (cancelled) return;
      setMap(resolved.map);
      setPathMap(resolved.pathMap);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isGoverned]);

  return { map, pathMap, loading, isGoverned };
};

export default usePagePermission;
